/**
 * voice-v8 — Werkzeugkarten: Kalender: Termine auflisten, anlegen, löschen.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3);
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, baueKarte, kartenTitel, kuerzeListe, leerHinweis, linkZeile, weitereHinweis, wertZeile } from "../karte.js";
import { icon } from "../icons.js";
import { beschreibungZeilen, dauerText, ordneNachTagen, type Eintrag, type Tag, type Termin } from "../kalender-tage.js";
import { sicheresZiel } from "../mail-text.js";
import { adressenSchwaerzen } from "../../../shared/adressen-schwaerzen.js";
import { adressenImText } from "../../../shared/link-erkennung.js";
import { datumKurz, tagPlus, WOCHENTAGE_KURZ, WOCHENTAGE_LANG, wienTag, wienUhr, wochentag } from "../../../shared/wien-zeit.js";
import { fmtDateTime, hostVon, trimText, unwrapResult } from "./hilfe.js";

// --- Calendar ------------------------------------------------------------
//
// Die Terminliste (20.10.2026, Martin: „schön dargestellt, gegebenenfalls mehrere Tage“): je Tag eine Gruppe mit
// Überschrift (Heute/Morgen/Wochentag + Datum), Ganztägiges oben, dann die Termine in Zeitfolge — Uhrzeit links, Titel,
// Ort und Dauer darunter. Heute: Vorbeigegangenes gedämpft, „Jetzt“ und „Als Nächstes“ benannt. Freie Tage sind
// EINE Zeile. Beschreibung, Teilnehmer und Links klappen je Termin auf (nur, was da ist). Alle Tage und Uhrzeiten
// in Wiener Zeit; die Rechnung steht in lib/kalender-tage.ts.

let tagNr = 0;

function sichererLink(href: string, text: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.textContent = text;
  return a;
}

/** Ein Text mit anklickbaren Adressen — nur http(s) und mailto (sicheresZiel, wie bei der Mail). */
function textMitLinks(eltern: HTMLElement, text: string): void {
  let zuletzt = 0;
  for (const t of adressenImText(text)) {
    if (t.start > zuletzt) eltern.append(text.slice(zuletzt, t.start));
    // Nur Web-Adressen: aus Fremdtext wird kein mailto:-Link.
    const ziel = sicheresZiel(t.ziel);
    if (ziel?.startsWith("http")) eltern.appendChild(sichererLink(ziel, t.text));
    else eltern.append(t.text);
    zuletzt = t.start + t.text.length;
  }
  if (zuletzt < text.length) eltern.append(text.slice(zuletzt));
}

/** Ein Link als Zeile mit Tippfläche ≥ 44 px; null bei unsicherer Adresse. */
function linkKnopf(roh: unknown, label: string): HTMLAnchorElement | null {
  const ziel = typeof roh === "string" ? sicheresZiel(roh.trim()) : null;
  if (!ziel?.startsWith("http")) return null;
  const a = sichererLink(ziel, "");
  a.className = "tool-kal-link";
  const lbl = document.createElement("span");
  lbl.textContent = label;
  a.append(lbl, icon("external", { size: 14 }));
  return a;
}

function span(klasse: string, text: string): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = klasse;
  s.textContent = text;
  return s;
}

/** Eine Adresse als Ort („https://meet.google.com/abc“) zeigt in der Zeile nur den Namen der Seite. */
function ortText(ort: string): string {
  const ziel = sicheresZiel(ort);
  return ziel?.startsWith("http") ? hostVon(ziel) || ort : ort;
}

/** Der Tag, an dem der Termin endet — 00:00 gehört noch zum Vortag (23:00–00:00 ist „bis 24:00“). */
function endetAm(e: Eintrag): string {
  return wienTag(new Date(e.ende!.getTime() - 1));
}

function endeUhr(ende: Date): string {
  const u = wienUhr(ende);
  return u === "00:00" ? "24:00" : u;
}

function zeitSpalte(e: Eintrag): HTMLElement {
  const z = document.createElement("span");
  z.className = "tool-kal-zeit";
  if (e.ganztag || e.fortsetzung) {
    z.appendChild(span("tool-kal-ganztag-wort", e.ganztag ? "Ganztägig" : "Läuft"));
    return z;
  }
  const von = document.createElement("time");
  von.className = "tool-kal-von";
  von.dateTime = e.beginn!.toISOString();
  von.textContent = wienUhr(e.beginn!);
  z.appendChild(von);
  if (e.ende && endetAm(e) === e.tag) z.appendChild(span("tool-kal-bis", `bis ${endeUhr(e.ende)}`));
  return z;
}

function metaText(e: Eintrag, jetzt: Date): string {
  const t = e.termin;
  const aus: string[] = [];
  const ort = t.location?.trim();
  if (ort) aus.push(trimText(ortText(ort), 90));
  if (e.ganztag) {
    if (e.letzterTag) aus.push(`bis ${WOCHENTAGE_KURZ[wochentag(e.letzterTag)]} ${datumKurz(e.letzterTag, jetzt)}`);
  } else if (e.ende) {
    if (endetAm(e) === e.tag) {
      // Letzter (oder einziger) Tag: Dauer nur beim eintägigen Termin, am Folgetag das Ende.
      if (e.fortsetzung) aus.push(`bis ${endeUhr(e.ende)}`);
      else {
        const d = dauerText(e.beginn!, e.ende);
        if (d) aus.push(d);
      }
    } else {
      const tag = endetAm(e);
      aus.push(`bis ${WOCHENTAGE_KURZ[wochentag(tag)]} ${datumKurz(tag, jetzt)}, ${endeUhr(e.ende)}`);
    }
  }
  return aus.join(", ");
}

/** Was nach dem Aufklappen dasteht — nur Vorhandenes. Null, wenn nichts da ist. */
function mehrInhalt(t: Termin): HTMLElement | null {
  const mehr = document.createElement("div");
  mehr.className = "tool-kal-mehr";
  const beschr = typeof t.description === "string" ? beschreibungZeilen(adressenSchwaerzen(t.description)) : [];
  if (beschr.length > 0) {
    const text = document.createElement("div");
    text.className = "tool-kal-text";
    for (const zeile of beschr) {
      const p = document.createElement("p");
      if (zeile) textMitLinks(p, zeile);
      else p.className = "tool-kal-luft";
      text.appendChild(p);
    }
    mehr.appendChild(text);
  }
  const namen = (Array.isArray(t.attendees) ? t.attendees : []).filter((n): n is string => typeof n === "string" && n.trim() !== "");
  if (namen.length > 0) {
    const p = document.createElement("div");
    p.className = "tool-kal-teilnehmer";
    const sicht = namen.slice(0, 8).map((n) => trimText(adressenSchwaerzen(n.trim()), 40));
    const mehrere = namen.length - sicht.length + (Number.isInteger(t.attendeesWeitere) ? Math.max(0, t.attendeesWeitere!) : 0);
    p.append(span("tool-kal-etikett", "Mit"), ` ${sicht.join(", ")}`);
    mehr.appendChild(p);
    if (mehrere > 0) {
      p.append(" ");
      weitereHinweis(p, mehrere);
    }
  }
  const ort = t.location?.trim();
  const treffpunkt = linkKnopf(t.hangoutLink, "Beitreten");
  const ortLink = ort && ort !== t.hangoutLink ? linkKnopf(ort, "Link öffnen") : null;
  const links = [treffpunkt, ortLink, linkKnopf(t.htmlLink, "Im Kalender öffnen")].filter((l): l is HTMLAnchorElement => l !== null);
  if (links.length > 0) {
    const zeile = document.createElement("div");
    zeile.className = "tool-kal-links";
    zeile.append(...links);
    mehr.appendChild(zeile);
  }
  return mehr.childElementCount > 0 ? mehr : null;
}

function terminEintrag(e: Eintrag, jetzt: Date): HTMLLIElement {
  const li = document.createElement("li");
  li.className = "tool-kal-termin";
  li.dataset.stand = e.stand || (e.ganztag ? "ganztag" : "");
  const mehr = mehrInhalt(e.termin);
  const huelle = document.createElement(mehr ? "details" : "div");
  huelle.className = "tool-kal-huelle";
  const kopf = document.createElement(mehr ? "summary" : "div");
  kopf.className = "tool-kal-kopf";
  kopf.appendChild(zeitSpalte(e));
  const rechts = document.createElement("span");
  rechts.className = "tool-kal-titelblock";
  const titel = document.createElement("span");
  titel.className = "tool-kal-titel";
  titel.textContent = trimText(e.termin.summary?.trim() || "(ohne Titel)", 120);
  rechts.appendChild(titel);
  if (e.stand === "jetzt") rechts.appendChild(span("tool-kal-marke", "Jetzt"));
  else if (e.stand === "naechster") rechts.appendChild(span("tool-kal-marke", "Als Nächstes"));
  else if (e.stand === "vorbei") rechts.appendChild(span("tool-kal-vorbei", "vorbei"));
  const meta = metaText(e, jetzt);
  if (meta) rechts.appendChild(span("tool-kal-meta", meta));
  kopf.appendChild(rechts);
  huelle.appendChild(kopf);
  if (mehr) huelle.appendChild(mehr);
  li.appendChild(huelle);
  return li;
}

/** „Heute“ / „Morgen“ / „Gestern“ / Wochentag, dazu das Datum. */
function tagKopf(tag: string, jetzt: Date): { name: string; datum: string } {
  const heute = wienTag(jetzt);
  const kurz = datumKurz(tag, jetzt);
  const rel = tag === heute ? "Heute" : tag === tagPlus(heute, 1) ? "Morgen" : tag === tagPlus(heute, -1) ? "Gestern" : null;
  return rel ? { name: rel, datum: `${WOCHENTAGE_KURZ[wochentag(tag)]} ${kurz}` } : { name: WOCHENTAGE_LANG[wochentag(tag)], datum: kurz };
}

function tagGruppe(t: Tag, jetzt: Date): HTMLElement {
  const k = tagKopf(t.tag, jetzt);
  if (t.frei) {
    const p = document.createElement("p");
    p.className = "tool-kal-frei";
    const ende = t.bis ? tagKopf(t.bis, jetzt) : null;
    p.append(span("tool-kal-tagname", ende ? `${k.name} ${k.datum} bis ${ende.name} ${ende.datum}` : `${k.name} ${k.datum}`), " ", span("tool-kal-frei-wort", "frei"));
    return p;
  }
  const sec = document.createElement("section");
  sec.className = "tool-kal-tag";
  if (t.tag === wienTag(jetzt)) sec.dataset.heute = "";
  const id = `tool-kal-tag-${++tagNr}`;
  const h = document.createElement("h4");
  h.className = "tool-kal-tagkopf";
  h.id = id;
  h.append(span("tool-kal-tagname", k.name), " ", span("tool-kal-datum", k.datum));
  sec.setAttribute("aria-labelledby", id);
  sec.appendChild(h);
  const ul = document.createElement("ul");
  ul.className = "tool-kal-liste";
  for (const e of [...t.ganztags, ...t.termine]) ul.appendChild(terminEintrag(e, jetzt));
  sec.appendChild(ul);
  return sec;
}

export function buildCalendarListCard(result: unknown, jetzt: Date = new Date()): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("calendar-list");
  const items = (Array.isArray(r.items) ? r.items : []).filter((x): x is Termin => typeof x === "object" && x !== null);
  const count = (r.count as number | undefined) ?? items.length;
  el.appendChild(kartenTitel(count === 1 ? "1 Termin" : `${count} Termine`));
  if (items.length === 0) {
    leerHinweis(el, "Keine Termine im Zeitraum.");
    return el;
  }
  const ordnung = ordneNachTagen(items, jetzt, { from: r.from, to: r.to, vollstaendig: r.vollstaendig });
  zeigeTage(el, ordnung.tage, jetzt);
  if (ordnung.unklar.length > 0) {
    const gekuerzt = kuerzeListe(ordnung.unklar, 3);
    const namen = gekuerzt.sichtbar.map((t) => trimText(t.summary?.trim() || "(ohne Titel)", 60)).join(", ");
    leerHinweis(el, `Ohne lesbare Zeit: ${namen}`);
    weitereHinweis(el, gekuerzt.rest);
  }
  if (r.vollstaendig === false) leerHinweis(el, "Die Liste ist gekürzt, es können weitere Termine folgen.");
  return el;
}

/** Höchstens so viele Zeilen insgesamt im zugeklappten Teil; der Rest steht als „und N weitere“. */
const DOM_MAX = 120;

/** Zeilen eines Tages, ganztägige zuerst. */
const zeilenVon = (t: Tag): Eintrag[] => [...t.ganztags, ...t.termine];

/** Eine zugeklappte Gruppe: nur ihre Kopfzeile ist zu sehen („und 5 weitere Termine“). */
function faltung(tage: readonly Tag[], text: string, jetzt: Date): HTMLDetailsElement {
  let frei = DOM_MAX;
  let verworfen = 0;
  const sichtbar: Tag[] = [];
  for (const t of tage) {
    const z = zeilenVon(t);
    if (z.length <= frei) {
      sichtbar.push(t);
      frei -= z.length;
      continue;
    }
    verworfen += z.length - frei;
    if (frei > 0) sichtbar.push({ ...t, ganztags: z.slice(0, frei).filter((e) => e.ganztag), termine: z.slice(0, frei).filter((e) => !e.ganztag) });
    frei = 0;
  }
  const d = document.createElement("details");
  d.className = "tool-kal-weitere";
  const s = document.createElement("summary");
  s.className = "tool-kal-weitere-kopf";
  s.textContent = text;
  d.appendChild(s);
  const tagesListe = document.createElement("div");
  tagesListe.className = "tool-kal-tage";
  for (const t of sichtbar) tagesListe.appendChild(tagGruppe(t, jetzt));
  d.appendChild(tagesListe);
  weitereHinweis(d, verworfen);
  return d;
}

function terminZahl(tage: readonly Tag[]): number {
  return tage.reduce((n, t) => n + zeilenVon(t).length, 0);
}

/**
 * Sichtbar bleibt EIN Tag — heute, sonst der erste mit Terminen (Martins Wunsch 03.07.: weniger Wucht) — mit höchstens
 * LISTE_MAX Zeilen. Frühere Tage, weitere Tage und der Rest eines langen Tages stehen zugeklappt dahinter.
 */
function zeigeTage(el: HTMLElement, tage: readonly Tag[], jetzt: Date): void {
  const heute = wienTag(jetzt);
  let anker = tage.findIndex((t) => t.tag === heute || (t.bis !== undefined && t.tag <= heute && heute <= t.bis));
  if (anker < 0) anker = tage.findIndex((t) => !t.frei);
  if (anker < 0) anker = 0;
  const wurzel = document.createElement("div");
  wurzel.className = "tool-kal-tage";
  const frueher = tage.slice(0, anker);
  if (terminZahl(frueher) > 0) wurzel.appendChild(faltung(frueher, `${terminZahl(frueher)} frühere Termine`, jetzt));
  else for (const t of frueher) wurzel.appendChild(tagGruppe(t, jetzt));
  const ankerTag = tage[anker];
  let rest: Tag | null = null;
  if (ankerTag) {
    const k = kuerzeListe(zeilenVon(ankerTag));
    if (k.rest > 0) {
      const eintraege = zeilenVon(ankerTag);
      wurzel.appendChild(tagGruppe({ ...ankerTag, ganztags: eintraege.slice(0, k.sichtbar.length).filter((e) => e.ganztag), termine: eintraege.slice(0, k.sichtbar.length).filter((e) => !e.ganztag) }, jetzt));
      rest = { ...ankerTag, ganztags: eintraege.slice(k.sichtbar.length).filter((e) => e.ganztag), termine: eintraege.slice(k.sichtbar.length).filter((e) => !e.ganztag) };
    } else wurzel.appendChild(tagGruppe(ankerTag, jetzt));
  }
  const spaeter = [...(rest ? [rest] : []), ...tage.slice(anker + 1)];
  if (spaeter.length > 0) {
    const n = terminZahl(spaeter);
    wurzel.appendChild(faltung(spaeter, n > 0 ? `und ${n} weitere Termine` : "Weitere Tage", jetzt));
  }
  el.appendChild(wurzel);
}

export function buildCalendarDeleteCard(result: unknown): HTMLElement {
  // 2026-07-03 (Martin): schlanke Ein-Zeilen-Karte statt Generic mit Roh-ID.
  const r = unwrapResult(result);
  const el = baueKarte("calendar-delete");
  const ok = r.ok !== false && !r.error;
  el.appendChild(
    kartenTitel(ok ? "Termin gelöscht" : "Löschen fehlgeschlagen"),
  );
  if (!ok && r.error) {
        el.appendChild(absatz(String(r.error), "warn"));
  }
  return el;
}

export function buildCalendarCreateCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("calendar-create");
  el.appendChild(kartenTitel("Termin angelegt"));
  if (r.summary) wertZeile(el, "Titel", trimText(String(r.summary), 160));
  if (r.start) wertZeile(el, "Beginn", fmtDateTime(String(r.start)));
  if (r.end) wertZeile(el, "Ende", fmtDateTime(String(r.end)));
  if (r.location) wertZeile(el, "Ort", trimText(String(r.location), 120));
  // Calendar-Tool liefert "link" als htmlLink-Alias.
  const link = (r.htmlLink ?? r.link) as string | undefined;
  if (link) {
    el.appendChild(linkZeile(String(link), "im Kalender öffnen"));
  }
  return el;
}
